// Leitura/escrita normalizada do JSONB swap_preferences.non_cash_composition.
// Tolera registros antigos (objeto vazio {} ou chaves ausentes) sem quebrar
// e nunca sobrescreve dados preenchidos pelo usuário com vazio.

export type DifferenceDirection = "receive" | "pay" | "both";
export type UrgencyLevel = "high" | "medium" | "low";

export interface NonCashComposition {
  accepts_vehicle: boolean;
  max_vehicle_value: number | null;
  vehicle_notes: string | null;
  accepts_multiple_properties: boolean;
  max_properties_count: number | null;
  accepts_other_goods: boolean;
  other_goods_description: string | null;
  difference_direction: DifferenceDirection | null;
  urgency: UrgencyLevel | null;
}

export const EMPTY_COMPOSITION: NonCashComposition = {
  accepts_vehicle: false,
  max_vehicle_value: null,
  vehicle_notes: null,
  accepts_multiple_properties: false,
  max_properties_count: null,
  accepts_other_goods: false,
  other_goods_description: null,
  difference_direction: null,
  urgency: null,
};

const toBool = (v: unknown): boolean => v === true;
const toNum = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const toStr = (v: unknown): string | null => {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
};
const toDir = (v: unknown): DifferenceDirection | null =>
  v === "receive" || v === "pay" || v === "both" ? v : null;
const toUrg = (v: unknown): UrgencyLevel | null =>
  v === "high" || v === "medium" || v === "low" ? v : null;

/**
 * Lê o JSONB de forma tolerante. Aceita:
 *  - null / undefined  → composição vazia
 *  - {} / objeto parcial → preenche chaves ausentes com defaults
 *  - tipos errados → coerção segura (nunca lança)
 */
export function readNonCashComposition(raw: unknown): NonCashComposition {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...EMPTY_COMPOSITION };
  }
  const r = raw as Record<string, unknown>;
  return {
    accepts_vehicle: toBool(r.accepts_vehicle),
    max_vehicle_value: toNum(r.max_vehicle_value),
    vehicle_notes: toStr(r.vehicle_notes),
    accepts_multiple_properties: toBool(r.accepts_multiple_properties),
    max_properties_count: toNum(r.max_properties_count),
    accepts_other_goods: toBool(r.accepts_other_goods),
    other_goods_description: toStr(r.other_goods_description),
    difference_direction: toDir(r.difference_direction),
    urgency: toUrg(r.urgency),
  };
}

/**
 * Verdadeiro se nenhum campo tem valor significativo. Útil para decidir
 * se devemos exibir o banner "Recadastre suas preferências" para registros
 * legados que ainda não migraram para o formato estruturado.
 */
export function isCompositionEmpty(c: NonCashComposition): boolean {
  return (
    !c.accepts_vehicle &&
    c.max_vehicle_value == null &&
    !c.vehicle_notes &&
    !c.accepts_multiple_properties &&
    c.max_properties_count == null &&
    !c.accepts_other_goods &&
    !c.other_goods_description &&
    c.difference_direction == null &&
    c.urgency == null
  );
}

/**
 * Serializa para gravar no JSONB. Sempre retorna objeto plano JSON-safe.
 * Mantém chaves false/null explícitas — a regra de imutabilidade do
 * trigger só protege contra `{}` puro.
 */
export function writeNonCashComposition(c: NonCashComposition): Record<string, unknown> {
  return { ...c };
}

/**
 * Mescla parcialmente: útil para atualizações granulares no editor.
 */
export function mergeNonCashComposition(
  current: NonCashComposition,
  patch: Partial<NonCashComposition>,
): NonCashComposition {
  return { ...current, ...patch };
}